import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ant design icons
import CloudUploadOutlined from '@ant-design/icons/CloudUploadOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';
import FileImageOutlined from '@ant-design/icons/FileImageOutlined';
import FileExcelOutlined from '@ant-design/icons/FileExcelOutlined';

// project imports
import { uploadIepDocument, listIepDocuments, deleteIepDocument } from 'services/api/iepMaterials';

const ACCEPT_ALL = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,application/pdf,image/*';
const ACCEPT_IMAGES_ONLY = '.png,.jpg,.jpeg,.gif,.webp,image/*';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];

const fileExt = (name) => (name?.split('.').pop() || '').toLowerCase();
const isImageFile = (name) => IMAGE_EXTENSIONS.includes(fileExt(name));

const iconForFile = (name) => {
  const ext = fileExt(name);
  if (ext === 'pdf') return <FilePdfOutlined />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined />;
  if (IMAGE_EXTENSIONS.includes(ext)) return <FileImageOutlined />;
  if (['doc', 'docx', 'txt'].includes(ext)) return <FileTextOutlined />;
  return <FileOutlined />;
};

/**
 * Upload + list + delete UI for IEP supporting documents (per VB-MAPP section).
 * Files are stored at: material-repo/iep/{supervisor}/{childId}/{section}/{filename}
 */
export default function IEPSectionUploads({ childId, section, readOnly = false, imageOnly = false, title, helpText, reloadToken = 0 }) {
  const theme = useTheme();
  const intl = useIntl();
  const inputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const fmt = (id, values) => intl.formatMessage({ id }, values);

  const loadFiles = useCallback(async () => {
    if (!childId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listIepDocuments(childId, section);
      setFiles(res?.files || []);
    } catch (err) {
      // Treat list-time failures as "no documents yet" — a missing section
      // directory is the normal state on a brand-new child, and we don't
      // want to spook the user with an error banner. Real outages will
      // surface again when the user tries to upload.
      console.warn('[IEPSectionUploads] list failed (treating as empty):', err?.response?.status, err?.message);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [childId, section]);

  /* RELOADS ON childId / section, AND WHENEVER THE PARENT SAYS SO.
   *
   * The parent gate matters for the IEP import. Applying an import mints the
   * assessment entry ids first and only then uploads the extracted grid images against
   * them — so these panels have already mounted and listed an empty directory by the
   * time the pictures land. Without `reloadToken` they sat on "No documents uploaded
   * yet" until something else happened to re-list, which is why uploading a second
   * image made the imported one appear alongside it: that upload's own refresh was the
   * first read that saw either file. */
  useEffect(() => {
    loadFiles();
  }, [loadFiles, reloadToken]);

  const handleUploadClick = () => inputRef.current?.click();

  const handleFilesSelected = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-uploading the same file later
    if (!list.length) return;
    if (!childId) {
      setError(fmt('iep.uploads.childIdMissing'));
      return;
    }

    setUploading(true);
    setError(null);
    try {
      for (const file of list) {
        setProgress(0);

        await uploadIepDocument({
          childId,
          section,
          file,
          onProgress: setProgress
        });
      }
      await loadFiles();
    } catch (err) {
      console.error('[IEPSectionUploads] upload failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (path) => {
    if (!window.confirm(fmt('iep.uploads.confirmDelete'))) return;
    setError(null);
    try {
      await deleteIepDocument(path);
      setFiles((prev) => prev.filter((f) => f.path !== path));
    } catch (err) {
      console.error('[IEPSectionUploads] delete failed:', err);
      setError(err?.response?.data?.detail || err?.message || 'Delete failed');
    }
  };

  const childIdMissing = !childId;

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: `1px dashed ${theme.palette.divider}`
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CloudUploadOutlined style={{ color: theme.palette.primary.main }} />
        <Typography variant="subtitle2" fontWeight={600}>
          {title || fmt(imageOnly ? 'iep.uploads.imagesTitle' : 'iep.uploads.title')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {!readOnly && (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudUploadOutlined />}
              onClick={handleUploadClick}
              disabled={uploading || childIdMissing}
            >
              {fmt('iep.uploads.uploadButton')}
            </Button>
            <input
              ref={inputRef}
              type="file"
              hidden
              multiple
              accept={imageOnly ? ACCEPT_IMAGES_ONLY : ACCEPT_ALL}
              onChange={handleFilesSelected}
            />
          </>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {helpText || fmt(imageOnly ? 'iep.uploads.imagesHelpText' : 'iep.uploads.helpText')}
      </Typography>

      {childIdMissing && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
          {fmt('iep.uploads.childIdMissing')}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {fmt('iep.uploads.uploading', { progress })}
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={20} />
        </Stack>
      ) : files.length === 0 ? (
        <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 1 }}>
          {fmt('iep.uploads.empty')}
        </Typography>
      ) : (
        <>
          {/* Image previews — thumbnails first so the user sees what was uploaded */}
          {files.filter((f) => isImageFile(f.name)).length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                mb: files.filter((f) => !isImageFile(f.name)).length > 0 ? 1.5 : 0.5
              }}
            >
              {files
                .filter((f) => isImageFile(f.name))
                .map((f) => (
                  <Box
                    key={f.path}
                    sx={{
                      position: 'relative',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: alpha(theme.palette.grey[900], 0.04),
                      width: '100%',
                      maxWidth: 800,
                      aspectRatio: '8 / 9',
                      cursor: 'zoom-in',
                      '&:hover .iep-thumb-actions': { opacity: 1 }
                    }}
                    onClick={() => window.open(f.url, '_blank', 'noopener,noreferrer')}
                  >
                    <Box
                      component="img"
                      src={f.url}
                      alt={f.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                      onError={(e) => {
                        // Fallback to file icon if the image fails to load
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    {/* Filename strip */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        px: 0.75,
                        py: 0.5,
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff'
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }} noWrap title={f.name}>
                        {f.name}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', opacity: 0.8 }}>
                        {formatBytes(f.size)}
                      </Typography>
                    </Box>
                    {/* Delete button on hover */}
                    {!readOnly && (
                      <Box
                        className="iep-thumb-actions"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          opacity: 0,
                          transition: 'opacity 0.2s'
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(f.path);
                          }}
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
            </Box>
          )}

          {/* Non-image files — keep the original list row layout */}
          {files.filter((f) => !isImageFile(f.name)).length > 0 && (
            <List dense disablePadding>
              {files
                .filter((f) => !isImageFile(f.name))
                .map((f) => (
                  <ListItem
                    key={f.path}
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      mb: 0.5,
                      pr: 1
                    }}
                    secondaryAction={
                      readOnly ? null : (
                        <IconButton edge="end" size="small" color="error" onClick={() => handleDelete(f.path)}>
                          <DeleteOutlined />
                        </IconButton>
                      )
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: theme.palette.text.secondary }}>{iconForFile(f.name)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Link
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ fontSize: '0.875rem', fontWeight: 500 }}
                        >
                          {f.name}
                        </Link>
                      }
                      secondary={formatBytes(f.size)}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
            </List>
          )}
        </>
      )}
    </Box>
  );
}

IEPSectionUploads.propTypes = {
  childId: PropTypes.string,
  // Section names follow [A-Za-z0-9][A-Za-z0-9_-]{0,127} on the backend —
  // accept any string here (e.g. 'milestones', 'barriers', 'assessment-<uuid>').
  section: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  // When true, restricts file picker to images only and uses image-flavored copy.
  imageOnly: PropTypes.bool,
  // Optional overrides for the panel title + help text.
  title: PropTypes.string,
  helpText: PropTypes.string,
  // Bump to force a re-list — used after an import writes files behind the panel's back.
  reloadToken: PropTypes.number
};
